import { defineComponent as _defineComponent } from 'vue'
import { translateLang } from '@/data/i18n/translateLang'
import EventBus, { EventBusKey } from '@/util/bus'
import { computed, ref } from 'vue'

import MFileItem from './MFileItem.vue'
import { categoryOptions, folderNameFillFields } from './data'
import type { File, Folder } from './types'
import { isInElement } from './utils'
import { FolderTreeManager } from './utils/folderControl'
import { createFolder } from './utils/folderFactory'


export default /*#__PURE__*/_defineComponent({
  __name: 'MFolderItem',
  props: {
    folder: null,
    activeId: null,
    folderManage: null,
    draggableData: null
  },
  setup(__props: any) {

const props = __props as {
  folder: Folder
  activeId?: string
  folderManage: FolderTreeManager
  draggableData?: Folder | File
}



const isTarget = ref(false)

const isRootFolder = computed(() => {
  return !props.folder.pid
})
const isActive = computed(() => {
  return props.activeId === props.folder.id
})

/**
 * @description 文件夹名称展示内容
 * 分类来源的标签整段使用分类文件夹图标的颜色高亮。
 */
const folderDisplayName = computed(() => {
  const { nameFields, name } = props.folder
  if (!nameFields?.length) return escapeHtml(name)
  // 拼接展示 HTML
  return nameFields
    .map((field) => {
      if (field.type === 'text') return escapeHtml(field.value)
      const tagName = getTagNameByValue(field.value) ?? field.value
      // 分类标签整段着色
      if (field.tagType !== 'classify') return escapeHtml(tagName)
      return `<span class="classify-tag-suffix">${escapeHtml(tagName)}</span>`
    })
    .join('')
})

/** @description 通过 value 反查标签展示名，优先普通来源，其次分类来源 */
function getTagNameByValue(value: string): string | undefined {
  return (
    folderNameFillFields.find((v) => v.value === value)?.name ??
    categoryOptions.find((v) => v.value === value)?.name
  )
}

/** @description 转义 HTML 特殊字符，防止 v-html 注入 */
function escapeHtml(str: string): string {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function getFolderIconType(folder: Folder) {
  const isOpen =
    (folder.children && folder.children.length > 0) ||
    (folder.accept.supportedFiles && folder.accept.supportedFiles.length > 0)
  return isOpen ? 'folder-open' : 'folder'
}
/** 添加子文件夹 */
function handleAddSubFolder() {
  // 基于当前目录创建一个新的文件夹，是其子文件夹
  const parentNode =
    (props.folderManage.getEntityById(props.folder.id) as Folder) ?? []
  const num = getNextFolderIndex(parentNode) || ''
  const folder = createFolder({
    pid: props.folder.id,
    name: translateLang('新建文件夹') + (num ? `(${num})` : ''),
  })
  EventBus.$emit(EventBusKey.CUSTOM_FOLDER_DRAG_EVENT, {
    type: 'add-folder',
    folder,
  })
}
/** 计算得到应该使用的新建文件夹的序号 */
function getNextFolderIndex(parentNode: Folder) {
  const prefix = translateLang('新建文件夹')
  const nums = []
  for (const { name } of parentNode.children) {
    if (!name.startsWith(prefix)) continue
    const matches = [...name.matchAll(/\((\d+)\)/g)]
    if (matches.length === 0) {
      nums.push(0) // 没数字 → 0
    } else {
      nums.push(Number(matches[matches.length - 1][1])) // 最后一个括号数字
    }
  }
  // 找最小缺失正整数
  const set = new Set(nums)
  let n = 0
  while (set.has(n)) n++
  return n
}
/** 修改文件夹信息 */
function handleEditFolder() {
  EventBus.$emit(EventBusKey.CUSTOM_FOLDER_DRAG_EVENT, {
    type: 'edit-folder',
    folder: props.folder,
  })
}

/** 删除单个文件 */
function handleDeleteFolder() {
  EventBus.$emit(EventBusKey.CUSTOM_FOLDER_DRAG_EVENT, {
    type: 'delete-folder',
    folder: props.folder,
  })
}
function handleDragStart(e: DragEvent) {
  e.dataTransfer?.setData(
    'application/json',
    JSON.stringify({ folder: props.folder })
  )
  EventBus.$emit(EventBusKey.CUSTOM_FOLDER_DRAG_EVENT, {
    type: 'drag-start',
    folder: props.folder,
  })
}

/** @description 拖动进入事件 */
function handleDragover(e: DragEvent) {
  const target = isInElement(e)
  if (!target) return
  // 需要放置文件的目标文件夹
  const pid = target.dataset.folderId as string
  // 是否此 ID 能够放入某个文件夹
  const isInFolder = props.folderManage.isAllowedInFolder(
    props.draggableData?.id ?? '',
    pid
  )
  // 其实上面的方法内部是包含这个判断的，但是每次新拖入的文件 ID 是重新创建的，所以第一步就弹出
  const isExistSameNameFile =
    props.draggableData &&
    props.folderManage.isExistSameNameFile(props.draggableData, pid)
  if (!isInFolder || isExistSameNameFile) {
    return
  }
  isTarget.value = pid === props.folder.id
}
/** @description 拖动离开事件 */
function handleDragLeave(e: DragEvent) {
  const target = isInElement(e)
  if (!target) return
  isTarget.value = false
}

/** @description 拖动结束通知外部添加文件 */
function handleDrop(e: DragEvent) {
  const target = isInElement(e)
  if (!target) return
  const folderId = target.dataset.folderId
  if (folderId !== props.folder.id) {
    return
  }
  isTarget.value = false
  // 拖动元素上携带的数据，点击元素时其实也有记录一份
  const data = JSON.parse(
    (e.dataTransfer?.getData('application/json') as string) ?? '{}'
  )
  // 根据拖拽元素不同获取不同的字段信息
  const type = data.file ? 'file' : 'folder'
  // 同步父级 ID
  data[type].pid = folderId
  // 不能放置在其本身或者子文件夹
  if (!props.folderManage.isAllowedInFolder(data[type].id, folderId)) {
    return
  }
  // 停止拖动通知外部进行文件添加操作
  EventBus.$emit(EventBusKey.CUSTOM_FOLDER_DRAG_EVENT, {
    type: `add-${type}`,
    [type]: data[type],
  })
}
function handleClickFolder() {
  EventBus.$emit(EventBusKey.CUSTOM_FOLDER_DRAG_EVENT, {
    type: 'active',
    id: props.folder.id,
  })
}

return { __sfc: true,props, isTarget, isRootFolder, isActive, folderDisplayName, getTagNameByValue, escapeHtml, getFolderIconType, handleAddSubFolder, getNextFolderIndex, handleEditFolder, handleDeleteFolder, handleDragStart, handleDragover, handleDragLeave, handleDrop, handleClickFolder, MFileItem }
}

})